<template>
  <div class="source-content themes">
    <div class="filter-bar">
      <div class="tb-form-group">
        <i class="icon icon-magnifier"></i>

        <input-tag
          placeholder="Search"
          :tags="tags"
          data-entity="producer-assets-searchThemesInput"
          @add="addTag"
          @remove="removeTag"
        />

        <button
          class="tb-btn tb-btn-contained medium-em margin-left-md"
          :class="{'disabled': !selected.length }"
          :disabled="!selected.length"
          data-entity="producer-assets-addSelectedThemesToPlaylist"
          @click="addSelectedToPlaylist('Themes')"
        >
          Add to playlist
        </button>
      </div>
    </div>

    <!-- No themes after searching -->
    <div v-if="!isLoading && !total && tags.length" class="text-center">
      <h3 class="margin-bottom-md">
        No themes found
      </h3>

      <button
        class="tb-btn tb-btn-contained medium-em"
        data-entity="producer-assets-resetThemeSearch"
        @click="clearTags"
      >
        Reset Search
      </button>
    </div>

    <div
      ref="sourceList"
      class="source-list"
    >
      <draggable
        v-model="themes"
        :clone="clone"
        :sort="false"
        animation="200"
        :group="{ name: 'timeline', pull: 'clone', put: false }"
        revert-on-spill="true"
        @start="startDrag"
        @end="$store.dispatch('clearDragType')"
      >
        <div
          v-for="theme in themes"
          class="source-item theme"
          :data-template-id="theme._id"
          data-entity="producer-assets-theme"
          :key="theme._id"
        >
          <div class="tb-checkbox">
            <input
              type="checkbox"
              :id="theme._id"
              :value="theme"
              v-model="selected"
              data-entity="producer-assets-toggleThemeSelection"
            >

            <label class="checkbox" :for="theme._id" />
          </div>

          <div class="source-item-wrapper">
            <div class="source-title title-text">
              {{ theme.title }}
            </div>

            <div class="source-description description-text capitalize">
              {{ theme.layout }}
            </div>

            <div class="source-actions margin-left-xxs">
              <button
                class="tb-btn tb-btn-contained sm medium-em add-to-playlist"
                title="Add to playlist"
                data-entity="producer-assets-addThemeToPlaylist"
                @click="addToPlaylist(theme, 'Theme')"
              >
                <span class="icon icon-plus" />
              </button>
            </div>
          </div>
        </div>
      </draggable>

      <div ref="loadTrigger" class="load-trigger">
        <div v-if="isLoading" class="loading loading--inline">
          <i class="icon icon-spinner"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { pick } from 'lodash-es';

import tabMixins from '../../../mixins/tab-mixins';

const { v1: uuidv1 } = require('uuid');

export default {
  mixins: [tabMixins],

  created() {
    this.$store.dispatch('getThemes');
  },

  mounted() {
    if (this.$refs.sourceList) {
      this.intersectionObserver = new IntersectionObserver(this.loadMore, {
        root: this.$refs.sourceList,
      });

      this.intersectionObserver.observe(this.$refs.loadTrigger);
    }
  },

  watch: {
    themes() {
      if (!this.$refs.sourceList || !this.themes.length) {
        return;
      }

      if (this.$refs.sourceList.scrollHeight <= this.$refs.sourceList.offsetHeight) {
        // List isn't scrollable, so try to load more right away
        // Otherwise the intersection observer will never trigger it's callback
        this.loadMore();
      }
    },
  },

  methods: {
    addTag(tag) {
      this.$store.dispatch('addThemeTag', tag);
    },

    removeTag(tag) {
      this.$store.dispatch('removeThemeTag', tag);
    },

    clearTags() {
      this.$store.dispatch('clearThemeTags');
    },

    /**
    * Attempt to load more themes
    */
    loadMore() {
      if (!this.isLoading && this.$store.getters.hasMoreThemes) {
        this.$store.dispatch('incrementThemesPage');
      }
    },

    startDrag(e) {
      const { item } = e;
      const { templateId } = item.dataset;

      const grabGroup = !!this.selected.find((t) => t._id === parseInt(templateId, 10));
      this.$store.dispatch('setDragType', {
        type: 'theme',
        group: grabGroup ? this.selected.map(this.clone) : null,
      });
    },

    clone(original) {
      const clone = pick(original, ['_id', 'title', 'layout']);
      clone.posts = [];
      clone.fields = {};
      clone.timeline_id = uuidv1();
      clone.displayTime = 30000;
      clone.transition = 'Cut'; // legacy but keep for backwards compatibility during testing
      clone.queuedPost = null;
      return clone;
    },

    reloadThemes() {
      this.$store.dispatch('resetThemes');
      this.$store.dispatch('getThemes');
    },
  },

  computed: {
    ...mapState({
      tags: (state) => state.themes.tags,
      isLoading: (state) => state.themes.isLoading,
      total: (state) => state.themes.total,
      themes: (state) => state.themes.themes,
    }),
  },
};
</script>
